import React, { useEffect, useMemo, useState } from 'react';

export type SubmissionQueueFilter =
  | 'needs_action'
  | 'missing_signal'
  | 'missing_evidence'
  | 'gate_review'
  | 'quality_review'
  | 'ready'
  | 'all';

interface TeacherSubmissionsQueuePanelProps {
  initialFilter?: SubmissionQueueFilter;
  studentOnly?: boolean;
  submissionsInbox: any;
  submissionsError: string | null;
  submissionsLoading: boolean;
  hideAbsentStudents: boolean;
  hiddenAbsentCount: number;
  expandedSubmissionStudentIds: Set<number>;
  redoRequested: number[];
  currentStepId: number | null;
  isFocusViewport: boolean;
  onRefresh: () => void;
  onToggleHideAbsentStudents: (nextValue: boolean) => void;
  onApproveEvidence: (evidenceId: number) => void;
  onGateDecision: (gateId: number, decision: 'allow' | 'revise', studentId: number) => void;
  onQualityDecision: (
    checkId: number,
    decision: 'clear' | 'assign_makeup' | 'group_reteach',
    studentId: number,
  ) => void;
  onRequestRedo: (studentId: number, stepId: number) => void;
  onToggleExpanded: (studentId: number) => void;
  formatSignalResponse: (response: Record<string, any> | null | undefined) => string;
}

interface QueueRow {
  studentId: number;
  studentName: string;
  row: any;
  priority: number;
  matches: Set<SubmissionQueueFilter>;
  badges: string[];
}

const FILTER_LABELS: Record<SubmissionQueueFilter, string> = {
  needs_action: 'Needs action',
  missing_signal: 'Missing signal',
  missing_evidence: 'Missing evidence',
  gate_review: 'Gate review',
  quality_review: 'Quality review',
  ready: 'Ready',
  all: 'All',
};

const classifySubmissionRow = (rowRaw: any, requiresEvidence: boolean): QueueRow => {
  const row = rowRaw && typeof rowRaw === 'object' ? rowRaw : {};
  const studentId = Number(row?.student_id || 0);
  const studentName = String(row?.student_name || `Student ${studentId || 'unknown'}`);
  const latestSignal = row?.signal?.latest;
  const latestEvidence = row?.evidence?.latest;
  const gate = row?.reflection_gate && typeof row.reflection_gate === 'object' ? row.reflection_gate : null;
  const quality = row?.submission_quality && typeof row.submission_quality === 'object' ? row.submission_quality : null;
  const gateStatus = String(gate?.final_status || '').trim().toLowerCase();
  const qualityStatus = String(quality?.status || '').trim().toLowerCase();
  const missingSignal = !latestSignal;
  const missingEvidence = requiresEvidence && !latestEvidence;
  const gateReview = gateStatus === 'needs_teacher_review' || gateStatus === 'blocked_revision';
  const qualityReview = qualityStatus === 'needs_teacher_review' || qualityStatus === 'provisional';
  const ready = !missingSignal && !missingEvidence && !gateReview && !qualityReview;
  const matches = new Set<SubmissionQueueFilter>(['all']);
  const badges: string[] = [];

  if (missingSignal) {
    matches.add('missing_signal');
    matches.add('needs_action');
    badges.push('Missing signal');
  }
  if (missingEvidence) {
    matches.add('missing_evidence');
    matches.add('needs_action');
    badges.push('Missing evidence');
  }
  if (gateReview) {
    matches.add('gate_review');
    matches.add('needs_action');
    badges.push(gateStatus === 'blocked_revision' ? 'Revision required' : 'Gate review');
  }
  if (qualityReview) {
    matches.add('quality_review');
    matches.add('needs_action');
    badges.push(qualityStatus === 'needs_teacher_review' ? 'Quality review' : 'Quality provisional');
  }
  if (ready) {
    matches.add('ready');
    badges.push('Ready');
  }

  let priority = 6;
  if (gateStatus === 'blocked_revision') priority = 0;
  else if (gateStatus === 'needs_teacher_review') priority = 1;
  else if (qualityStatus === 'needs_teacher_review') priority = 2;
  else if (qualityStatus === 'provisional') priority = 3;
  else if (missingEvidence) priority = 4;
  else if (missingSignal) priority = 5;

  return {
    studentId,
    studentName,
    row,
    priority,
    matches,
    badges,
  };
};

const buildQueueRows = (submissionsInbox: any): QueueRow[] => {
  const requiresEvidence = Boolean(submissionsInbox?.step?.counts_as_evidence);
  const rows = Array.isArray(submissionsInbox?.submissions) ? submissionsInbox.submissions : [];
  return rows
    .map((rowRaw: any) => classifySubmissionRow(rowRaw, requiresEvidence))
    .sort((left: QueueRow, right: QueueRow) => {
      if (left.priority !== right.priority) return left.priority - right.priority;
      const leftTriage = Number(left.row?.triage_score || 0);
      const rightTriage = Number(right.row?.triage_score || 0);
      if (leftTriage !== rightTriage) return rightTriage - leftTriage;
      return left.studentName.localeCompare(right.studentName);
    });
};

export const countSubmissionNeedsAction = (submissionsInbox: any): number => (
  buildQueueRows(submissionsInbox).filter((row) => row.matches.has('needs_action')).length
);

const formatTimestamp = (value?: string | null): string => (
  value ? new Date(value).toLocaleString() : 'No timestamp'
);

export const TeacherSubmissionsQueuePanel: React.FC<TeacherSubmissionsQueuePanelProps> = ({
  initialFilter,
  studentOnly,
  submissionsInbox,
  submissionsError,
  submissionsLoading,
  hideAbsentStudents,
  hiddenAbsentCount,
  expandedSubmissionStudentIds,
  redoRequested,
  currentStepId,
  isFocusViewport,
  onRefresh,
  onToggleHideAbsentStudents,
  onApproveEvidence,
  onGateDecision,
  onQualityDecision,
  onRequestRedo,
  onToggleExpanded,
  formatSignalResponse,
}) => {
  const [activeFilter, setActiveFilter] = useState<SubmissionQueueFilter>(initialFilter || 'needs_action');
  const [selectedStudentId, setSelectedStudentId] = useState<number | null>(null);

  const queueRows = useMemo(() => buildQueueRows(submissionsInbox), [submissionsInbox]);
  const filteredRows = useMemo(() => {
    if (activeFilter === 'all') return queueRows;
    return queueRows.filter((row) => row.matches.has(activeFilter));
  }, [activeFilter, queueRows]);
  const selectedRow = useMemo(
    () => filteredRows.find((row) => row.studentId === selectedStudentId) || filteredRows[0] || null,
    [filteredRows, selectedStudentId],
  );

  useEffect(() => {
    if (!selectedRow) {
      setSelectedStudentId(null);
      return;
    }
    if (selectedStudentId !== selectedRow.studentId) {
      setSelectedStudentId(selectedRow.studentId);
    }
  }, [selectedRow, selectedStudentId]);

  const summary = submissionsInbox?.summary || {};
  const filterCounts = useMemo<Record<SubmissionQueueFilter, number>>(() => {
    const counts: Record<SubmissionQueueFilter, number> = {
      needs_action: 0,
      missing_signal: 0,
      missing_evidence: 0,
      gate_review: 0,
      quality_review: 0,
      ready: 0,
      all: queueRows.length,
    };
    queueRows.forEach((row) => {
      (Object.keys(counts) as SubmissionQueueFilter[]).forEach((key) => {
        if (key === 'all') return;
        if (row.matches.has(key)) counts[key] += 1;
      });
    });
    return counts;
  }, [queueRows]);

  const renderDetail = (queueRow: QueueRow) => {
    const row = queueRow.row;
    const safeStudentId = queueRow.studentId;
    const latestSignal = row?.signal?.latest;
    const latestEvidence = row?.evidence?.latest;
    const gate = row?.reflection_gate && typeof row.reflection_gate === 'object' ? row.reflection_gate : null;
    const quality = row?.submission_quality && typeof row.submission_quality === 'object' ? row.submission_quality : null;
    const gateReasonCodes = Array.isArray(gate?.reason_codes) ? gate.reason_codes : [];
    const qualityReasonCodes = Array.isArray(quality?.reason_codes) ? quality.reason_codes : [];
    const gateSuggestions = Array.isArray(gate?.suggestions) ? gate.suggestions : [];
    const qualitySuggestions = Array.isArray(quality?.suggestions) ? quality.suggestions : [];
    const signalReasoning = String(
      latestSignal?.response?.reasoning_text
      || latestSignal?.response?.reasoning
      || '',
    ).trim();
    const signalConfidenceRaw = latestSignal?.response?.confidence_level ?? latestSignal?.response?.confidence;
    const signalConfidence = signalConfidenceRaw !== undefined && signalConfidenceRaw !== null
      ? Number(signalConfidenceRaw)
      : null;
    const evidenceText = String(latestEvidence?.content?.text || latestEvidence?.content?.url || '').trim();
    const gateStatus = String(gate?.final_status || '').trim().toLowerCase();
    const qualityStatus = String(quality?.status || '').trim().toLowerCase();
    const gateAllowed = gateStatus === 'allowed';
    const gateBlocked = gateStatus === 'blocked_revision';
    const qualityResolved = qualityStatus === 'resolved';
    const qualityAccepted = qualityStatus === 'accepted';
    const gateId = Number(gate?.id || 0);
    const qualityId = Number(quality?.id || 0);
    const canActOnGate = Number.isInteger(gateId) && gateId > 0 && safeStudentId > 0;
    const canActOnQuality = Number.isInteger(qualityId) && qualityId > 0 && safeStudentId > 0;
    const canRequestRedo = Boolean(currentStepId && safeStudentId > 0);
    const isExpanded = expandedSubmissionStudentIds.has(safeStudentId);

    return (
      <div className="space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">{queueRow.studentName}</h3>
            <p className="mt-1 text-sm text-slate-600">
              Signal attempts: {Number(row?.signal?.attempt_count || 0)} | Evidence attempts: {Number(row?.evidence?.attempt_count || 0)}
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {queueRow.badges.map((badge) => (
                <span
                  key={`${queueRow.studentId}-${badge}`}
                  className="rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-700"
                >
                  {badge}
                </span>
              ))}
              {Number(row?.triage_score || 0) > 0 ? (
                <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-800">
                  Triage {Number(row?.triage_score || 0)}
                </span>
              ) : null}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {latestEvidence && String(latestEvidence.status || '').toLowerCase() === 'submitted' ? (
              <button
                type="button"
                onClick={() => onApproveEvidence(latestEvidence.id)}
                className="rounded bg-emerald-100 px-3 py-1.5 text-xs text-emerald-700 hover:bg-emerald-200"
              >
                Approve
              </button>
            ) : null}
            {gate && !gateAllowed ? (
              <button
                type="button"
                onClick={() => onGateDecision(gateId, 'allow', safeStudentId)}
                className="rounded bg-sky-100 px-3 py-1.5 text-xs text-sky-700 hover:bg-sky-200 disabled:opacity-50"
                disabled={!canActOnGate}
              >
                Allow reflection
              </button>
            ) : null}
            {gate && !gateBlocked ? (
              <button
                type="button"
                onClick={() => onGateDecision(gateId, 'revise', safeStudentId)}
                className="rounded bg-rose-100 px-3 py-1.5 text-xs text-rose-700 hover:bg-rose-200 disabled:opacity-50"
                disabled={!canActOnGate}
              >
                Require revision
              </button>
            ) : null}
            {quality && !qualityResolved ? (
              <button
                type="button"
                onClick={() => onQualityDecision(qualityId, 'clear', safeStudentId)}
                className="rounded bg-emerald-100 px-3 py-1.5 text-xs text-emerald-700 hover:bg-emerald-200 disabled:opacity-50"
                disabled={!canActOnQuality}
              >
                Clear quality
              </button>
            ) : null}
            {quality && !qualityResolved ? (
              <button
                type="button"
                onClick={() => onQualityDecision(qualityId, 'assign_makeup', safeStudentId)}
                className="rounded bg-amber-100 px-3 py-1.5 text-xs text-amber-700 hover:bg-amber-200 disabled:opacity-50"
                disabled={!canActOnQuality}
              >
                Assign makeup
              </button>
            ) : null}
            {quality && !qualityResolved ? (
              <button
                type="button"
                onClick={() => onQualityDecision(qualityId, 'group_reteach', safeStudentId)}
                className="rounded bg-indigo-100 px-3 py-1.5 text-xs text-indigo-700 hover:bg-indigo-200 disabled:opacity-50"
                disabled={!canActOnQuality}
              >
                Group reteach
              </button>
            ) : null}
            {canRequestRedo ? (
              <button
                type="button"
                onClick={() => onRequestRedo(safeStudentId, Number(currentStepId))}
                className="rounded bg-amber-100 px-3 py-1.5 text-xs text-amber-700 hover:bg-amber-200"
              >
                {redoRequested.includes(safeStudentId) ? 'Redo requested' : 'Request redo'}
              </button>
            ) : null}
          </div>
        </div>

        <div className="grid gap-4 xl:grid-cols-2">
          <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-indigo-700">Latest signal</p>
            <p className="mt-2 whitespace-pre-wrap break-words text-sm text-indigo-950">
              {formatSignalResponse(latestSignal?.response || null)}
            </p>
            <p className="mt-2 text-xs text-indigo-700">{formatTimestamp(latestSignal?.submitted_at)}</p>
            {signalReasoning ? (
              <p className="mt-2 whitespace-pre-wrap break-words text-xs text-indigo-900">Reasoning: {signalReasoning}</p>
            ) : null}
            {signalConfidence !== null && Number.isFinite(signalConfidence) ? (
              <p className="mt-1 text-xs text-indigo-900">Confidence: {signalConfidence}</p>
            ) : null}
          </div>

          <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Latest evidence</p>
            <p className="mt-2 whitespace-pre-wrap break-words text-sm text-emerald-950">{evidenceText || '—'}</p>
            <p className="mt-2 text-xs text-emerald-700">
              Status: {String(latestEvidence?.status || 'none')}
              {latestEvidence?.submitted_at ? ` | ${new Date(latestEvidence.submitted_at).toLocaleString()}` : ''}
            </p>
          </div>
        </div>

        {(gateReasonCodes.length > 0 || qualityReasonCodes.length > 0 || gateSuggestions.length > 0 || qualitySuggestions.length > 0) ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            {gateReasonCodes.length > 0 ? (
              <p>Gate reasons: {gateReasonCodes.join(', ')}</p>
            ) : null}
            {qualityReasonCodes.length > 0 ? (
              <p className="mt-1">Quality reasons: {qualityReasonCodes.join(', ')}</p>
            ) : null}
            {gateSuggestions.length > 0 ? (
              <p className="mt-1">Gate suggestion: {String(gateSuggestions[0])}</p>
            ) : null}
            {qualitySuggestions.length > 0 ? (
              <p className="mt-1">Quality suggestion: {String(qualitySuggestions[0])}</p>
            ) : null}
            {qualityAccepted ? (
              <p className="mt-1">Quality accepted.</p>
            ) : null}
          </div>
        ) : null}

        {(Number(row?.signal?.attempt_count || 0) > 1 || Number(row?.evidence?.attempt_count || 0) > 1) ? (
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <button
              type="button"
              className="text-xs text-slate-600 hover:text-slate-900"
              onClick={() => onToggleExpanded(safeStudentId)}
            >
              {isExpanded ? 'Hide full attempt history' : 'Show full attempt history'}
            </button>
            {isExpanded ? (
              <div className="mt-4 grid gap-4 xl:grid-cols-2">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Signal attempts</p>
                  <div className="mt-2 space-y-2">
                    {(Array.isArray(row?.signal?.recent_attempts) ? row.signal.recent_attempts : []).slice(1).map((attempt: any) => (
                      <div key={`signal-attempt-${attempt.id}`} className="text-xs text-slate-700">
                        <div>{formatTimestamp(attempt.submitted_at)}</div>
                        <div className="whitespace-pre-wrap break-words">{formatSignalResponse(attempt.response || null)}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Evidence attempts</p>
                  <div className="mt-2 space-y-2">
                    {(Array.isArray(row?.evidence?.recent_attempts) ? row.evidence.recent_attempts : []).slice(1).map((attempt: any) => (
                      <div key={`evidence-attempt-${attempt.id}`} className="text-xs text-slate-700">
                        <div>{formatTimestamp(attempt.submitted_at)}</div>
                        <div>Status: {String(attempt.status || 'submitted')}</div>
                        <div className="whitespace-pre-wrap break-words">
                          {String(attempt.content?.text || attempt.content?.url || '—')}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  };

  if (studentOnly) return <section aria-label="Current response review" className="space-y-4">
    <div className="flex items-center justify-between gap-3"><p className="text-sm text-slate-600">Current step: {submissionsInbox?.step?.title || 'Loading response context'}</p><button className="rounded border px-3 py-2 text-sm" onClick={onRefresh}>Refresh response</button></div>
    {submissionsError && <p role="alert">{submissionsError}</p>}
    {submissionsLoading ? <p role="status">Loading response…</p> : selectedRow ? renderDetail(selectedRow) : <p>No response record for this student on the current step.</p>}
  </section>;

  return (
    <div data-testid="teacher-submissions-panel" className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Submissions</p>
          <p className="mt-1 text-sm text-slate-600">Compact queue for the current step.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={!hideAbsentStudents}
              onChange={(event) => onToggleHideAbsentStudents(!event.target.checked)}
              className="rounded border-slate-300"
            />
            Include absent students
          </label>
          <button
            type="button"
            className="rounded border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
            onClick={onRefresh}
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2 text-xs">
        <span className="rounded-full border border-slate-200 px-2 py-1 text-slate-700">
          Students: {Number(summary.students_total || 0)}
        </span>
        <span className="rounded-full border border-indigo-200 bg-indigo-50 px-2 py-1 text-indigo-700">
          Signal: {Number(summary.signals_submitted || 0)}
        </span>
        <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 text-emerald-700">
          Evidence: {Number(summary.evidence_submitted || 0)}
        </span>
        <span className="rounded-full border border-sky-200 bg-sky-50 px-2 py-1 text-sky-700">
          Both: {Number(summary.both_submitted || 0)}
        </span>
        <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-1 text-amber-700">
          Gate review: {Number(summary.gate_review || 0)}
        </span>
        <span className="rounded-full border border-rose-200 bg-rose-50 px-2 py-1 text-rose-700">
          Quality review: {Number(summary.quality_review || 0)}
        </span>
        {hideAbsentStudents && hiddenAbsentCount > 0 ? (
          <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-1 text-amber-700">
            Hidden absent: {hiddenAbsentCount}
          </span>
        ) : null}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {(Object.keys(FILTER_LABELS) as SubmissionQueueFilter[]).map((filter) => (
          <button
            key={`submission-filter-${filter}`}
            type="button"
            className={`rounded-full border px-3 py-1.5 text-sm transition ${
              activeFilter === filter
                ? 'border-indigo-300 bg-indigo-50 text-indigo-900'
                : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
            }`}
            onClick={() => setActiveFilter(filter)}
          >
            {FILTER_LABELS[filter]} ({filterCounts[filter]})
          </button>
        ))}
      </div>

      {submissionsError ? (
        <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {submissionsError}
        </div>
      ) : null}

      {submissionsLoading ? (
        <div className="mt-4 text-sm text-slate-500">Loading submissions...</div>
      ) : filteredRows.length === 0 ? (
        <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
          No students in the {FILTER_LABELS[activeFilter].toLowerCase()} view.
        </div>
      ) : isFocusViewport ? (
        <div className="mt-4 space-y-3">
          {filteredRows.map((queueRow) => {
            const selected = selectedRow?.studentId === queueRow.studentId;
            return (
              <div key={`submission-row-mobile-${queueRow.studentId}`} className="rounded-xl border border-slate-200">
                <button
                  type="button"
                  className={`flex w-full items-start justify-between gap-3 rounded-xl px-4 py-3 text-left ${
                    selected ? 'bg-indigo-50' : 'bg-white'
                  }`}
                  onClick={() => setSelectedStudentId(selected ? null : queueRow.studentId)}
                >
                  <div>
                    <p className="text-sm font-medium text-slate-900">{queueRow.studentName}</p>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {queueRow.badges.map((badge) => (
                        <span
                          key={`mobile-badge-${queueRow.studentId}-${badge}`}
                          className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] text-slate-700"
                        >
                          {badge}
                        </span>
                      ))}
                    </div>
                  </div>
                  <span className="text-xs text-slate-500">{selected ? 'Hide' : 'View'}</span>
                </button>
                {selected ? (
                  <div className="border-t border-slate-200 px-4 py-4">
                    {renderDetail(queueRow)}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="mt-4 grid gap-4 xl:grid-cols-[320px_minmax(0,1fr)]">
          <div data-testid="submission-queue" className="max-h-[60vh] space-y-2 overflow-y-auto rounded-xl border border-slate-200 bg-slate-50 p-3">
            {filteredRows.map((queueRow) => {
              const selected = selectedRow?.studentId === queueRow.studentId;
              return (
                <button
                  key={`submission-row-desktop-${queueRow.studentId}`}
                  type="button"
                  className={`w-full rounded-xl border px-3 py-3 text-left transition ${
                    selected
                      ? 'border-indigo-300 bg-indigo-50'
                      : 'border-slate-200 bg-white hover:bg-slate-50'
                  }`}
                  onClick={() => setSelectedStudentId(queueRow.studentId)}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-slate-900">{queueRow.studentName}</p>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {queueRow.badges.map((badge) => (
                          <span
                            key={`desktop-badge-${queueRow.studentId}-${badge}`}
                            className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] text-slate-700"
                          >
                            {badge}
                          </span>
                        ))}
                      </div>
                    </div>
                    {Number(queueRow.row?.triage_score || 0) > 0 ? (
                      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] text-amber-800">
                        {Number(queueRow.row?.triage_score || 0)}
                      </span>
                    ) : null}
                  </div>
                </button>
              );
            })}
          </div>
          <div data-testid="submission-detail" className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            {selectedRow ? renderDetail(selectedRow) : (
              <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-8 text-sm text-slate-500">
                Select a student to view submission detail.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
